import { OctagonAlert, X } from 'lucide-react'
import { toast as customToast } from '@/components/custom-toast'
import { toast as sonnerToast } from 'sonner'
import { useCallback, useEffect, useRef, useState } from 'react'

export const outsideOverageArea = {
  key: 'outside_the_coverage_area',
  title: 'Oops, You’re Outside the Coverage Area',
  descriptions: {
    line1: `You have selected an area outside the current scope of the platform.
          Please try again, keeping your search within the State of California.`,
    line2: `We are working hard to open new regions. If there is a state you would like to see us open, please contact us at: `,
  },
}
export const showOutsideOverageAreaToast = () =>
  customToast.custom({
    renderLayout: (toastId) => (
      <OutsideOverageAreaToast duration={6000} toastID={toastId} />
    ),
    duration: 100000,
  })

const OutsideOverageAreaToast = ({
  duration,
  toastID,
}: {
  duration: number
  toastID: string | number
}) => {
  const [progress, setProgress] = useState(100)
  const [isPaused, setIsPaused] = useState(false)
  const startTimeRef = useRef(Date.now())
  const pauseTimeRef = useRef(0)
  const totalPausedTimeRef = useRef(0)
  const animationFrameRef = useRef<number>()

  const updateProgress = useCallback(() => {
    const currentTime = Date.now()
    const elapsedTime =
      currentTime - startTimeRef.current - totalPausedTimeRef.current
    const newProgress = Math.max(0, 100 - (elapsedTime * 100) / duration)

    setProgress(newProgress)

    if (newProgress > 0) {
      animationFrameRef.current = requestAnimationFrame(updateProgress)
    } else {
      sonnerToast.dismiss(toastID)
    }
  }, [duration, toastID])

  useEffect(() => {
    if (isPaused) {
      pauseTimeRef.current = Date.now()
      cancelAnimationFrame(animationFrameRef.current!)
    } else {
      if (pauseTimeRef.current > 0) {
        totalPausedTimeRef.current += Date.now() - pauseTimeRef.current
        pauseTimeRef.current = 0
      }
      animationFrameRef.current = requestAnimationFrame(updateProgress)
    }

    return () => {
      cancelAnimationFrame(animationFrameRef.current!)
    }
  }, [isPaused, updateProgress])

  const handleMouseEnter = () => {
    return setIsPaused(true)
  }
  const handleMouseLeave = () => setIsPaused(false)
  const handleClose = () => {
    cancelAnimationFrame(animationFrameRef.current!)
    sonnerToast.dismiss(toastID)
  }

  return (
    <div
      className="flex w-full flex-col max-w-md overflow-hidden rounded-lg bg-white shadow-lg md:min-w-[372px] p-6"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#EF3826]/20 rounded-full">
            <OctagonAlert className="text-[#EF3826]" />
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {outsideOverageArea.title}
          </div>
        </div>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <div className="space-y-3 text-gray-600">
        <p className="leading-relaxed text-sm">
          {outsideOverageArea.descriptions.line1}
        </p>
        <p className="leading-relaxed text-sm">
          {outsideOverageArea.descriptions.line2}
          <a
            href="mailto:support@hydrascore.com"
            className="text-primary font-medium underline"
          >
            support@hydrascore.com
          </a>.
        </p>
      </div>

      <div className="mt-4 w-full bg-gray-200 h-1 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-[width] duration-100 ease-linear"
          style={{
            width: `${progress.toFixed(0)}%`,
          }}
        />
      </div>
    </div>
  )
}
